import React from 'react';
import {
    CloseCircleOutlined,
    DownloadOutlined,
    DownOutlined,
    FilterOutlined,
    SearchOutlined,
    ExclamationCircleOutlined
} from '@ant-design/icons';
import {
    Button,
    Input,
    List,
    Modal,
    Select,
    Table,
    DatePicker,
    Typography,
    Checkbox,
    Menu,
    Dropdown,
    Spin
} from 'antd';
import { Api } from '../../lib/api';
import './styles.scss';

import DataBrowserResultsModal from './components/DataBrowserResultsModal';
import LegacyMigration from './LegacyMigration';
import { hexEncode, downloadFile } from '../../lib/utils';

import {
    TEST_STATUS_FILTER_VALUE__PASS,
    TEST_STATUS_FILTER_VALUE__FAIL,
    TEST_STATUS_FILTER_VALUE__INCOMPLETE
} from '@shared/constants/constants';
import TestStatusBadge from './components/TestStatusBadge';

const { Text } = Typography;

const FILTER__DELETE_RESULTS = 7;
const FILTER__DOWNLOAD_RESULTS = 8;

/**
 * Sort priority for columns in the database browser table with multiple sorters.
 * Sorters are applied in reverse order, so the first sorter has the highest priority.
 * Part of the antd Table component's sorting api
 */
const sortPriority = {
    measurementDate: 1,
    serialNumber: 2
}

const EXCLUDED_DEVICE_TYPES = [
    1, // UNRECOGNIZED
    2, // DEMO
    5, // SX1
    6, // USB Test and Measurement Device (IVI)
    7, // Label Printer
    8 // File Store
];

interface FilterParams {
    serialNumber: string;
    testDate: { startDate?: string; endDate?: string };
    stationNames?: string[];
    deviceTypes?: string[];
    testStatuses?: string[];
    testPlanName?: string | null;
    customFields?: { name: string; values: string[] }[];
}

interface DatabaseBrowserState {
    loading: boolean;
    csvLoading: boolean;
    data: { results: any[]; totalRecords: number };
    pagination: { current: number; pageSize: number; total: number };
    displayedFilter: number | null;
    serialNumber: string;
    downloadUrl: string;
    testDateStart: any;
    testDateEnd: any;
    stations: string[];
    deviceTypes: string[];
    testStatuses: string[];
    customFields: Record<string, Record<string, boolean>>;
    customFieldName: string;
    customFieldValue: string;
    resultsPerPage: number;
    fileName: string;
    fileNameError: string;
    allStations: string[];
    allDeviceTypes: string[];
    allCustomFields: string[];
    selectedDut: any;
    legacyMigrationVisible: boolean;
    testPlanName: string | null;
    sorters: { field: string; order: string }[];
}

export default class DatabaseBrowser extends React.PureComponent<Record<string, never>, DatabaseBrowserState> {
    private searchInput: any;

    state: DatabaseBrowserState = {
        loading: false,
        csvLoading: false,
        data: { results: [], totalRecords: 0 },
        pagination: {
            current: 1,
            pageSize: 10,
            total: 0
        },
        displayedFilter: null,
        serialNumber: '',
        downloadUrl: '',
        testDateStart: null,
        testDateEnd: null,
        stations: [],
        deviceTypes: [],
        testStatuses: [],
        customFields: {},
        customFieldName: '',
        customFieldValue: '',
        resultsPerPage: 10,
        fileName: '',
        fileNameError: '',

        allStations: [],
        allDeviceTypes: [],
        allCustomFields: [],
        selectedDut: null,
        legacyMigrationVisible: false,
        testPlanName: null,
        sorters: [],
    };

    handleTableChange = (pagination, filters, sorter) => {
        this.handlePaginationChange(pagination);
        this.handleSorterChange(sorter);
    }

    handlePaginationChange = (pagination) => {
        this.setState(
            (state) => ({
                pagination: {
                    ...state.pagination,
                    current: pagination.current,
                    pageSize: pagination.pageSize
                },
                resultsPerPage: pagination.pageSize
            }),
            () => this.refreshResults()
        );
    }

    handleSorterChange = (sorter) => {
        const normalized = Array.isArray(sorter) ? sorter : [sorter];
        const activeSorters = normalized.filter(s => s.order);

        this.setState((state) => ({
                sorters: activeSorters.map(s => ({
                    field: s.field || s.columnKey || s.column.title,
                    order: s.order
                }))
            }),
            () => this.refreshResults()
        );
    }

    getFilters(): FilterParams {
        let filter: FilterParams = {
            serialNumber: '',
            testDate: {}
        };
        if (this.state.serialNumber !== '') {
            filter.serialNumber = hexEncode(this.state.serialNumber);
        }

        if (this.state.testDateStart !== null) {
            filter.testDate.startDate = this.state.testDateStart.format('YYYY-MM-DD 00:00:00');
        }

        if (this.state.testDateEnd !== null) {
            filter.testDate.endDate = this.state.testDateEnd.format('YYYY-MM-DD 23:59:59');
        }

        if (this.state.stations.length !== 0) {
            filter.stationNames = this.state.stations;
        }

        if (this.state.deviceTypes.length !== 0) {
            filter.deviceTypes = this.state.deviceTypes;
        }

        if (this.state.testStatuses.length !== 0) {
            filter.testStatuses = this.state.testStatuses;
        }

        let customFields: { name: string; values: string[] }[] = [];
        for (let field in this.state.customFields) {
            let values = Object.keys(this.state.customFields[field]);

            if (values.length > 0) {
                let v = values.map((v) => hexEncode(v));
                customFields.push({
                    name: field,
                    values: v
                });
            }
        }

        if (this.state.testPlanName !== null) {
            filter.testPlanName = this.state.testPlanName;
        }

        if (customFields.length > 0) {
            filter.customFields = customFields;
        }
        return filter;
    }

    getSorterString() {
        if (this.state.sorters.length === 0) {
            return '';
        }

        let sorters = this.state.sorters.map((sorter) => (`${sorter.field}:${sorter.order === 'ascend' ? 'ASC' : 'DESC'}`));

        return sorters.join(',');
    }

    private columnFilterProps(dataIndex: string) {
        return {
            filterIcon: (filtered: boolean) => (
                <SearchOutlined style={{ color: filtered ? '#1890ff' : '#0072CE' }} />
            ),
            onFilter: (value: any, record: any) =>
                record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
        };
    }

    private renderTextInputFilter(config: {
        placeholder: string;
        value: string | null;
        setSelectedKeys: (keys: React.Key[]) => void;
        onChange: (value: string) => void;
    }) {
        return (
            <div style={{ padding: 8, marginTop: -110 }}>
                <Input
                    ref={(node) => { this.searchInput = node; }}
                    placeholder={config.placeholder}
                    value={config.value ?? ''}
                    onChange={(e) => {
                        config.setSelectedKeys(e.target.value ? [e.target.value] : []);
                        config.onChange(e.target.value);
                    }}
                    style={{ width: 188, marginBottom: 8, display: 'block' }}
                    autoFocus
                />
            </div>
        );
    }

    private renderCheckboxFilter(config: {
        options: string[];
        value: string[];
        onChange: (values: string[]) => void;
    }) {
        return (
            <div style={{ padding: 8, marginTop: -110 }}>
                <div style={{ width: 188, marginBottom: 8, display: 'block' }}>
                    <Checkbox.Group
                        options={config.options}
                        onChange={(checkedValues) => config.onChange(checkedValues as string[])}
                        value={config.value}
                    />
                </div>
            </div>
        );
    }

    private renderStationFilter() {
        return (
            <div style={{ padding: 8, marginTop: -110 }}>
                <div style={{ width: 188, marginBottom: 8, display: 'block' }}>
                    <Select
                        mode="multiple"
                        placeholder="Search by stations"
                        value={this.state.stations}
                        onChange={(stations) => {
                            this.setState(
                                { stations, displayedFilter: null },
                                () => this.refreshResults(1)
                            );
                        }}
                        style={{ width: '100%' }}
                    >
                        {
                            this.state.allStations
                            .filter((o) => !this.state.stations.includes(o))
                            .map((item, i) => (
                                <Select.Option key={item + i} value={item}>
                                    {item}
                                </Select.Option>
                            ))
                        }
                    </Select>
                </div>
            </div>
        );
    }

    private renderCustomFieldsFilter(customFieldsList?: { field: string; value: string }[]) {
        return (
            <div style={{ padding: 8, marginTop: -110 }}>
                <div className="custom-fields-modal">
                    <div className="field">
                        <div>Field:</div>
                        <Select
                            value={this.state.customFieldName}
                            onChange={(customFieldName) => this.setState({ customFieldName })}
                        >
                            {this.state.allCustomFields.map((item) => (
                                <Select.Option key={item} value={item}>
                                    {item}
                                </Select.Option>
                            ))}
                        </Select>
                    </div>
                    <div className="value">
                        <div>Value:</div>
                        <Input
                            value={this.state.customFieldValue}
                            onChange={(e) => this.setState({ customFieldValue: e.target.value })}
                        />
                    </div>
                    <div className="add">
                        <Button
                            onClick={() => {
                                this.setState((state) => {
                                    let customFields = { ...state.customFields };
                                    const fieldName = state.customFieldName;
                                    const fieldValue = state.customFieldValue;

                                    if (fieldName in customFields) {
                                        customFields[fieldName] = { ...customFields[fieldName] };
                                    } else {
                                        customFields[fieldName] = {};
                                    }

                                    customFields[fieldName][fieldValue] = true;

                                    return { customFields, displayedFilter: null };
                                }, () => this.refreshResults(1));
                            }}
                        >
                            Add
                        </Button>
                    </div>
                    <div className="list">
                        <List
                            size="small"
                            bordered
                            dataSource={customFieldsList}
                            renderItem={(item: { field: string; value: string }) => (
                                <List.Item>
                                    <div className="custom-field-list-item">
                                        <div>{item.field + ': ' + item.value}</div>
                                        <div
                                            className="custom-field-delete"
                                            onClick={() => {
                                                this.setState((state) => {
                                                    let customFields = {
                                                        ...state.customFields,
                                                        [item.field]: { ...state.customFields[item.field] }
                                                    };

                                                    delete customFields[item.field][item.value];
                                                    if (Object.keys(customFields[item.field]).length === 0) {
                                                        delete customFields[item.field];
                                                    }

                                                    return { customFields, displayedFilter: null };
                                                }, () => this.refreshResults(1));
                                            }}
                                        >
                                            <CloseCircleOutlined />
                                        </div>
                                    </div>
                                </List.Item>
                            )}
                        />
                    </div>
                </div>
            </div>
        );
    }

    private renderDateFilter() {
        return (
            <div style={{ padding: 8, marginTop: -110 }}>
                <div style={{ width: 188, marginBottom: 8, display: 'block' }}>
                    <div>From:</div>
                    <DatePicker
                        value={this.state.testDateStart}
                        onChange={(date) => {
                            if (this.state.testDateEnd) {
                                this.setState(
                                    { testDateStart: date },
                                    () => this.refreshResults(1)
                                );
                            } else {
                                this.setState({ testDateStart: date });
                            }
                        }}
                    />
                    <div>To:</div>
                    <DatePicker
                        value={this.state.testDateEnd}
                        onChange={(date) => {
                            if (this.state.testDateStart) {
                                this.setState(
                                    { testDateEnd: date },
                                    () => this.refreshResults(1)
                                );
                            } else {
                                this.setState({ testDateEnd: date });
                            }
                        }}
                    />
                </div>
            </div>
        );
    }

    refreshResults(page?: number) {
        this.setState((state) => ({
            loading: true,
            pagination: {
                ...state.pagination,
                pageSize: this.state.resultsPerPage
            }
        }));

        Api.getDataBrowser({
            resultsPerPage: this.state.resultsPerPage,
            page: page ? page : this.state.pagination.current,
            filterParams: this.getFilters(),
            sort: this.getSorterString(),
        }).then((results) => {
            this.setState((state) => ({
                loading: false,
                data: results,
                pagination: {
                    ...state.pagination,
                    total: results.totalRecords
                }
            }));
        });
    }

    componentDidMount() {
        Api.getStations().then((stations) => this.setState({ allStations: stations.map((s) => s.name) }));
        Api.getDeviceTypes()
            .then((deviceTypes) =>
                deviceTypes
                    .filter((d) => !EXCLUDED_DEVICE_TYPES.includes(d.id))
                    .map((d) => d.name)
                    .sort((a, b) => a.localeCompare(b))
            )
            .then((deviceTypes) => this.setState({ allDeviceTypes: deviceTypes }));
        Api.getCustomFields()
            .then((customFields) => customFields.map((c) => c.name).sort((a, b) => a.localeCompare(b)))
            .then((customFields) => this.setState({ allCustomFields: customFields }));
        this.refreshResults();
    }
    
    validateFileName(fileName) {
        if (!fileName || fileName.trim() === '') {
            return 'Filename cannot be empty';
        }

        // Check for invalid characters
        const invalidChars = /[<>:"/\\|?*\x00-\x1f\x7f]/g;
        const matches = fileName.match(invalidChars);

        if (matches) {
            const uniqueChars = [...new Set(matches)].join(' ');
            return `Invalid characters not allowed: ${uniqueChars}`;
        }

        // Windows reserves these names even when followed by an extension
        // (e.g. CON.csv is still reserved), so check the base name.
        const baseName = fileName.split('.')[0];
        const reserved = /^(CON|PRN|AUX|NUL|COM[0-9]|LPT[0-9])$/i;
        if (reserved.test(baseName)) {
            return 'This filename is reserved by Windows';
        }

        // Windows silently strips trailing dots and spaces from filenames,
        // which can turn a unique name into a duplicate or empty one.
        if (/[. ]$/.test(fileName)) {
            return 'Filename cannot end with a space or period';
        }

        // Check length (account for the .csv extension appended on download)
        if (fileName.length > 250) {
            return 'Filename is too long (max 250 characters)';
        }

        return '';
    }

    handleFileNameChange(value) {
        const error = this.validateFileName(value);
        this.setState({
            fileName: value,
            fileNameError: error
        });
    }

    async downloadFile() {
        this.setState({
            csvLoading: true,
            displayedFilter: null
        });

        try {
            const blob = await Api.getDataBrowserCsv({
                filterParams: this.getFilters(),
                detailed: true,
                fileName: this.state.fileName
            });

            downloadFile(`${this.state.fileName}.csv`, blob);

            this.setState({ csvLoading: false });
        } catch (error) {
            console.error('CSV export failed:', error);
            alert('Failed to download CSV. Please try again.');
            this.setState({ csvLoading: false });
        }
    }

    openInNewTab = (url) => {
        const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
        if (newWindow) newWindow.opener = null;
    };

    clearFilters = () => {
        this.setState(
            {
                serialNumber: '',
                testDateStart: null,
                testDateEnd: null,
                stations: [],
                deviceTypes: [],
                testStatuses: [],
                customFields: {},
                customFieldName: '',
                customFieldValue: '',
                resultsPerPage: 10
            },
            () => this.refreshResults()
        );
    }

    render() {
        let customFields: { field: string; value: string }[] = [];

        Object.keys(this.state.customFields)
            .sort((a, b) => a.localeCompare(b))
            .forEach((field) => {
                Object.keys(this.state.customFields[field])
                    .sort((a, b) => a.localeCompare(b))
                    .forEach((value) => {
                        customFields.push({ field, value });
                    });
            });

        return (
            <div className="database-browser">
                <div className="page-header">
                    <Button
                        icon={<FilterOutlined />}
                        shape="round"
                        disabled={
                            this.state.serialNumber === '' &&
                            this.state.deviceTypes.length === 0 &&
                            this.state.stations.length === 0 &&
                            this.state.testDateStart === null &&
                            this.state.testDateEnd === null &&
                            Object.keys(this.state.customFields).length === 0 &&
                            this.state.testStatuses.length === 0
                        }
                        onClick={this.clearFilters}
                    >
                        Clear Filters
                    </Button>
                    <Button
                        type="primary"
                        shape="round"
                        icon={<DownloadOutlined />}
                        onClick={() => {
                            this.setState({
                                displayedFilter: FILTER__DOWNLOAD_RESULTS,
                                fileNameError: this.state.fileName
                                    ? this.validateFileName(this.state.fileName)
                                    : ''
                            });
                        }}
                    >
                        Download CSV
                    </Button>
                    <Modal
                        title={
                            <div className="modal-header">
                                <div>Download Results</div>
                            </div>
                        }
                        wrapClassName="download-results-modal"
                        open={this.state.displayedFilter === FILTER__DOWNLOAD_RESULTS}
                        onCancel={() => this.setState({ displayedFilter: null })}
                        closable={true}
                        destroyOnClose={true}
                        mask={true}
                        footer={
                            <div className="footer">
                                <div className="buttons">
                                    <Button
                                        type="primary"
                                        onClick={() => this.downloadFile()}
                                        disabled={this.state.fileName === '' || this.state.fileNameError !== ''}
                                    >
                                        Download
                                    </Button>
                                    <Button onClick={() => this.setState({ displayedFilter: null })}> Cancel</Button>
                                </div>
                            </div>
                        }
                    >
                        <div>File Name:</div>
                        <Input
                            value={this.state.fileName}
                            onChange={(e) => this.handleFileNameChange(e.target.value)}
                            autoFocus
                            status={this.state.fileNameError ? 'error' : ''}
                        />
                        {this.state.fileNameError && (
                            <div style={{ color: '#E60013', marginTop: '4px', fontSize: '14px' }}>
                                {this.state.fileNameError}
                            </div>
                        )}
                        {this.state.data.totalRecords > 100000 && (
                            <div className="warning">
                                <ExclamationCircleOutlined />
                                &nbsp;Warning: Selected filter contains {this.state.data.totalRecords} DUTs. Only results from the first 100000 will be downloaded.</div>
                        )}
                    </Modal>
                    <Modal visible={this.state.csvLoading} footer={null} closable={false}>
                        <div style={{ textAlign: 'center' }}>
                            <Spin size="large" />
                            <div style={{ marginTop: '16px' }}>
                                Generating CSV File...
                            </div>
                        </div>
                    </Modal>
                    <Dropdown
                        overlay={
                            <Menu
                                onClick={(e) => {
                                    if (e.key === '1') {
                                        this.setState((state) => ({
                                            displayedFilter: FILTER__DELETE_RESULTS
                                        }));
                                    } else if (e.key === '2') {
                                        this.setState({ legacyMigrationVisible: true });
                                    }
                                }}
                            >
                                <Menu.Item key="2">Import Legacy Results</Menu.Item>
                            </Menu>
                        }
                    >
                        <Button shape="round">
                            Actions <DownOutlined />
                        </Button>
                    </Dropdown>

                    {this.state.legacyMigrationVisible && (
                        <LegacyMigration
                            legacyMigrationVisible={this.state.legacyMigrationVisible}
                            closeLegacyMigration={(data, callback) => this.setState(data, callback)}
                        />
                    )}
                </div>
                <div className="data-card results">
                    <Table
                        columns={[
                            {
                                title: () => (
                                    <div>
                                        Serial Number
                                        {this.state.serialNumber && (
                                            <span className="filter-style">: {this.state.serialNumber}</span>
                                        )}
                                    </div>
                                ),
                                dataIndex: 'serialNumber',
                                sorter: { multiple: sortPriority.serialNumber },
                                ...this.columnFilterProps('serialNumber'),
                                filterDropdown: ({ setSelectedKeys }: any) =>
                                    this.renderTextInputFilter({
                                        placeholder: 'Search serialNumber',
                                        value: this.state.serialNumber,
                                        setSelectedKeys,
                                        onChange: (val) => this.setState(
                                            { serialNumber: val, displayedFilter: null },
                                            () => this.refreshResults(1)
                                        ),
                                    }),
                                render: (text, record) => (
                                    <React.Fragment>
                                        <Text className='serial-number'>{text} </Text>
                                    </React.Fragment>
                                )
                            },
                            {
                                title: (
                                    <div>
                                        Test Plans
                                    </div>
                                ),
                                dataIndex: 'testBlockResults',
                                ...this.columnFilterProps('testPlanNames'),
                                filterDropdown: ({ setSelectedKeys }: any) =>
                                    this.renderTextInputFilter({
                                        placeholder: 'Search Test Plans',
                                        value: this.state.testPlanName,
                                        setSelectedKeys,
                                        onChange: (val) => this.setState(
                                            { testPlanName: val, displayedFilter: null },
                                            () => this.refreshResults(1)
                                        ),
                                    }),
                                render: (result, record) => (
                                    <div className='table-container'>
                                        {
                                            result.map((r, index) => (
                                                <div key={index}>{r.testPlanName}</div>
                                            ))
                                        }
                                    </div>
                                )
                            },
                            {
                                title: (
                                    <div>
                                        Type
                                        {this.state.deviceTypes.length !== 0 && (
                                            <React.Fragment>
                                                :{' '}
                                                {this.state.deviceTypes.map((dev, index) => (
                                                    <span className="filter-style" key={index}>
                                                        {dev} &nbsp;
                                                    </span>
                                                ))}
                                            </React.Fragment>
                                        )}
                                    </div>
                                ),
                                dataIndex: 'testBlockResults',
                                ...this.columnFilterProps('type'),
                                filterDropdown: () =>
                                    this.renderCheckboxFilter({
                                        options: ['IL/RL', 'Polarity'],
                                        value: this.state.deviceTypes,
                                        onChange: (values) => this.setState(
                                            { deviceTypes: values, displayedFilter: null },
                                            () => this.refreshResults(1)
                                        ),
                                    }),
                                render: (results, index) => (
                                    <div className='table-container'>
                                        {results
                                            .slice(0)
                                            .map((result) => {
                                                return (
                                                    <React.Fragment key={result.id}>
                                                        <div>
                                                            {result.type === 'RL1'
                                                                ? 'IL/RL'
                                                                : result.type === 'PT1'
                                                                ? 'Polarity'
                                                                : result.type}
                                                        </div>
                                                    </React.Fragment>
                                                );
                                            })}
                                    </div>
                                )
                            },
                            {
                                title: 'Station',
                                ...this.columnFilterProps('station'),
                                filterDropdown: () => this.renderStationFilter(),
                                render: (text, record) => (
                                    <div className='table-container'>
                                        {record.testBlockResults
                                            .slice(0)
                                            .map((result, index) => {
                                                return (
                                                    <React.Fragment key={result.id}>
                                                        <div>{result.stationName}</div>
                                                    </React.Fragment>
                                                );
                                            })}
                                    </div>
                                )
                            },
                            {
                                title: (
                                    <div>
                                        Summary{' '}
                                        {this.state.testStatuses.length !== 0 && (
                                            <React.Fragment>
                                                :{' '}
                                                {this.state.testStatuses.map((testStatus, index) => (
                                                    <span className="filter-style" key={index}>
                                                        {testStatus} &nbsp;
                                                    </span>
                                                ))}
                                            </React.Fragment>
                                        )}
                                    </div>
                                ),
                                ...this.columnFilterProps('summary'),
                                filterDropdown: () =>
                                    this.renderCheckboxFilter({
                                        options: [
                                            TEST_STATUS_FILTER_VALUE__PASS,
                                            TEST_STATUS_FILTER_VALUE__FAIL,
                                            TEST_STATUS_FILTER_VALUE__INCOMPLETE
                                        ],
                                        value: this.state.testStatuses,
                                        onChange: (values) => this.setState(
                                            { testStatuses: values, displayedFilter: null },
                                            () => this.refreshResults(1)
                                        ),
                                    }),
                                render: (text, record) => (
                                    <div className='table-container'>
                                        {record.testBlockResults
                                            .slice(0)
                                            .map((result) => {
                                                return (
                                                    <React.Fragment key={result.id}>
                                                        <div>
                                                            <TestStatusBadge testStatus={result.testStatus} inline />
                                                        </div>
                                                    </React.Fragment>
                                                );
                                            })}
                                    </div>
                                )
                            },
                            {
                                title: 'Measurement Date',
                                sorter: { multiple: sortPriority.measurementDate },
                                ...this.columnFilterProps('date'),
                                filterDropdown: () => this.renderDateFilter(),
                                render: (text, record) => (
                                    <div className='table-container'>
                                        {record.testBlockResults
                                            .slice(0)
                                            .map((result) => {
                                                return (
                                                    <React.Fragment key={result.id}>
                                                        <div className="createdAt">{result.createdAt}</div>
                                                    </React.Fragment>
                                                );
                                            })}
                                    </div>
                                )
                            },
                            {
                                title: 'Custom Fields',
                                ...this.columnFilterProps('Custom Fields'),
                                filterDropdown: () => this.renderCustomFieldsFilter(customFields),
                                render: (text, record) => (
                                    <div>
                                        {record.testBlockResults
                                            .slice(0)
                                            .map((result) => {
                                                let customFields = JSON.parse(result.fieldResponses);

                                                return (
                                                    <React.Fragment key={result.id}>
                                                        <div>
                                                            {Object.keys(customFields)
                                                                .map((key) => key + ': ' + customFields[key])
                                                                .join(', ')}
                                                        </div>
                                                    </React.Fragment>
                                                );
                                            })}
                                    </div>
                                )
                            }
                        ]}
                        rowKey={(result) => result.id}
                        dataSource={this.state.data.results}
                        pagination={this.state.pagination}
                        onChange={this.handleTableChange}
                        onRow={(record, rowIndex) => {
                            return {
                                onClick: () => {
                                    this.setState({
                                        selectedDut: record
                                    });
                                }
                            };
                        }}
                        loading={this.state.loading}
                        sticky={true}
                    />
                </div>
                {this.state.selectedDut !== null && (
                    <DataBrowserResultsModal
                        selectedDut={this.state.selectedDut}
                        serialNumber={this.state.selectedDut.serialNumber}
                        testBlockResults={this.state.selectedDut.testBlockResults}
                        openInNewTab={this.openInNewTab}
                        close={() => this.setState({selectedDut: null})}
                    >
                    </DataBrowserResultsModal>
                )}
            </div>
        );
    }
}
